/* Exercise session controller: question flow, attempts, scoring, timers and results.
   Plain TypeScript with a subscribe/snapshot API; ExerciseRunner renders it. */
import type { ReactNode } from 'react';
import { audio, type Player } from '../../core/audio';
import { debugEnabled, exposeDebug } from '../../core/debug';
import { Emitter } from '../../core/events';
import { isMicOn, type MicState, onMic, startMic } from '../../core/mic';
import { addXp, award, type LevelState, saveLevel } from '../../core/store';
import { chordSymbol, noteName, pretty } from '../../core/theory';
import { type Tone, toast } from '../../core/toast';
import { choice, scrollTop } from '../../core/util';
import { Rich, richText } from '../../ui/Rich';
import type { ExerciseConfig, Explain, FinalDetail, InputMode, Question, QuestionApi, SessionStats } from './types';

export interface ActiveQuestion {
  /** unique per question; with `mount` it keys the question component */
  id: number;
  q: Question;
  attempts: number;
  done: boolean;
  hinted: boolean;
  hintIdx: number;
  maxMiss?: number;
  prompt: string;
  explain?: Explain;
  reveal?: () => string | null;
  /** bumped to re-mount the question (input mode change) */
  mount: number;
  api: QuestionApi;
  playing: boolean;
  /** session ends after this question */
  ended: boolean;
}

export interface SessionResult {
  acc: number;
  stars: number;
  aborted: boolean;
  prev: LevelState;
  newBest: boolean;
}

export type Phase = 'start' | 'run' | 'results';

export function answerTextOf(q: Question): string {
  if (q.answerText) return q.answerText();
  if (q.type === 'notes') return q.target.map((m) => noteName(m, { flat: q.flat })).join(' · ');
  if (q.type === 'chord') return pretty(q.target.sym || chordSymbol(q.target.root, q.target.q, q.flat));
  return '';
}

const PRAISE = ['Doğru!', 'Harika!', 'Tam isabet!', 'Çok iyi!'];

export class ExerciseSession {
  readonly cfg: ExerciseConfig;
  readonly needsMic: boolean;
  phase: Phase = 'start';
  st: SessionStats;
  current: ActiveQuestion | null = null;
  feedback: { content: ReactNode; tone?: Tone } | null = null;
  result: SessionResult | null = null;
  deadline = 0;
  private qid = 0;
  private version = 0;
  private events = new Emitter<{ change: number }>();
  private timer: ReturnType<typeof setInterval> | undefined;
  private autoNext: ReturnType<typeof setTimeout> | undefined;
  private autoPlay: ReturnType<typeof setTimeout> | undefined;
  private offMic: (() => void) | null = null;
  private player: Player | null = null;
  private onExit: () => void;
  private begun = false;
  private attached = false;

  constructor(cfg: ExerciseConfig, onExit: () => void) {
    this.cfg = cfg;
    this.onExit = onExit;
    this.needsMic = !!cfg.input && cfg.input !== 'none';
    this.st = {
      i: 0,
      answered: 0,
      credit: 0,
      xp: 0,
      streak: 0,
      bestStreak: 0,
      score: 0,
      lives: cfg.lives || null,
      review: [],
      over: false,
      mode: cfg.mode || 'practice',
      inputMode: isMicOn() ? 'mic' : 'screen',
    };
  }

  // ------------------------------------------------------------ store API
  subscribe = (fn: () => void): (() => void) => this.events.on('change', fn);
  getVersion = (): number => this.version;
  private changed(): void {
    this.version++;
    this.events.emit('change', this.version);
  }

  /** Seconds left in a timed session. */
  timeLeft(): number {
    return Math.max(0, (this.deadline - performance.now()) / 1000);
  }

  // ------------------------------------------------------------ flow
  begin = async (): Promise<void> => {
    if (this.begun) return;
    this.begun = true;
    await audio.resume();
    this.st.inputMode = isMicOn() ? 'mic' : 'screen';
    this.phase = 'run';
    scrollTop();
    if (this.cfg.timeLimit) this.deadline = performance.now() + this.cfg.timeLimit * 1000;
    this.startListeners();
    this.changed();
    this.nextQuestion();
  };

  /** Timers and mic listeners of a running session; safe to call repeatedly. */
  private startListeners(): void {
    if (!this.attached || this.phase !== 'run' || this.st.over) return;
    if (this.needsMic && !this.offMic) this.offMic = onMic('state', (s) => this.onMicState(s));
    if (this.cfg.timeLimit && this.timer === undefined) {
      this.timer = setInterval(() => {
        this.changed();
        if (performance.now() >= this.deadline) this.finish(false);
      }, 200);
    }
  }

  nextQuestion = (): void => {
    clearTimeout(this.autoNext);
    clearTimeout(this.autoPlay);
    const { st, cfg } = this;
    if (st.over) return;
    if ((cfg.total && st.answered >= cfg.total) || (st.lives != null && st.lives <= 0)) {
      this.finish(false);
      return;
    }
    let q: Question;
    try {
      q = cfg.make(st.i, st);
    } catch (e) {
      console.error(e);
      this.finish(false);
      return;
    }
    st.i++;
    const cq: ActiveQuestion = {
      id: ++this.qid,
      q,
      attempts: 0,
      done: false,
      hinted: false,
      hintIdx: 0,
      maxMiss: q.maxMiss,
      prompt: q.prompt,
      explain: q.explain,
      mount: 0,
      api: undefined as unknown as QuestionApi,
      playing: false,
      ended: false,
    };
    cq.api = this.makeApi(cq);
    this.current = cq;
    this.feedback = null;
    if (debugEnabled()) exposeDebug('question', { n: st.i, ...q, answerText: answerTextOf(q) });
    this.changed();
    if (q.play && q.autoplay !== false)
      this.autoPlay = setTimeout(() => {
        if (this.current === cq && !cq.done) this.play();
      }, 250);
  };

  /** Re-mounts the current question (e.g. after switching between mic and screen input). */
  restartQuestion(): void {
    const cq = this.current;
    if (cq && !cq.done) {
      cq.mount++;
      cq.api = this.makeApi(cq);
      this.feedback = null;
    }
    this.changed();
  }

  async setInputMode(mode: InputMode): Promise<void> {
    if (mode === 'mic' && !isMicOn()) {
      const ok = await startMic();
      if (!ok) {
        toast('Mikrofon açılamadı.', { tone: 'bad' });
        return;
      }
    }
    this.st.inputMode = mode;
    this.restartQuestion();
  }

  private onMicState(s: MicState): void {
    if (s === 'on' && this.st.inputMode === 'screen') {
      this.st.inputMode = 'mic';
      this.restartQuestion();
    } else if (s !== 'on' && this.st.inputMode === 'mic') {
      this.st.inputMode = 'screen';
      this.restartQuestion();
    } else this.changed();
  }

  play = (slow?: boolean): Player | undefined => {
    const cq = this.current;
    if (!cq) return undefined;
    audio.stopAll();
    const p = slow ? cq.q.playSlow?.() : cq.q.play?.();
    if (!p) return undefined;
    this.player = p;
    cq.playing = true;
    this.changed();
    p.done.then(() => {
      if (this.player === p) {
        cq.playing = false;
        this.changed();
      }
    });
    return p;
  };

  hint = (): void => {
    const cq = this.current;
    const hints = cq?.q.hints;
    if (!cq || !hints?.length || cq.done) return;
    const hn = hints[Math.min(cq.hintIdx, hints.length - 1)];
    cq.hintIdx++;
    cq.hinted = true;
    const out = hn?.run(cq.api);
    if (out) this.setFeedback(out, 'info');
    this.changed();
  };

  skip = (): void => {
    if (this.current && !this.current.done) this.finalize(false, { skipped: true });
  };

  private setFeedback(content: ReactNode, tone?: Tone): void {
    this.feedback =
      content == null ? null : { content: typeof content === 'string' ? <Rich text={content} /> : content, tone };
    this.changed();
  }

  private makeApi(cq: ActiveQuestion): QuestionApi {
    const input = this.st.inputMode;
    return {
      input,
      mode: this.st.mode,
      isDone: () => cq.done,
      play: (slow) => (this.current === cq ? this.play(slow) : undefined),
      feedback: (content, tone) => {
        if (this.current === cq) this.setFeedback(content, tone);
      },
      miss: (detail) => {
        if (cq.done) return;
        cq.attempts++;
        if (cq.maxMiss && cq.attempts >= cq.maxMiss) this.finalize(false, detail);
        else this.changed();
      },
      done: (correct, detail) => {
        if (!cq.done && this.current === cq) this.finalize(correct, detail);
      },
      setPrompt: (text) => {
        cq.prompt = text;
        this.changed();
      },
      setExplain: (fn) => {
        cq.explain = fn;
      },
      setReveal: (fn) => {
        cq.reveal = fn;
      },
      reveal: () => cq.reveal?.() ?? null,
      setMaxMiss: (n) => {
        cq.maxMiss = n;
      },
      countAttempt: () => {
        cq.attempts++;
      },
    };
  }

  finalize(correct: boolean, detail: FinalDetail = {}): void {
    const cq = this.current;
    if (!cq || cq.done) return;
    const { st, cfg } = this;
    const q = cq.q;
    cq.done = true;
    st.answered++;
    let credit = 0;
    let gained = 0;
    if (correct) {
      credit = cq.attempts === 0 ? (cq.hinted ? 0.6 : 1) : 0.5;
      if (detail.self) credit = Math.min(credit, 0.8);
      const clean = cq.attempts === 0 && !cq.hinted;
      const bonus = clean ? Math.min(10, Math.max(0, st.streak - 1) * 2) : 0;
      gained = (cq.attempts === 0 ? (cq.hinted ? 6 : 10) : 4) + bonus;
      st.streak = clean ? st.streak + 1 : 0;
      st.score += q.points || 1;
    } else {
      st.streak = 0;
      if (st.lives != null) st.lives--;
      st.review.push({ label: q.reviewLabel || richText(cq.prompt || ''), answer: answerTextOf(q) });
    }
    st.bestStreak = Math.max(st.bestStreak, st.streak);
    st.credit += credit;
    if (gained) {
      st.xp += gained;
      addXp(gained, cfg.module);
    }
    const msg = correct
      ? cq.attempts === 0 && !cq.hinted
        ? choice(PRAISE)
        : 'Doğru.'
      : detail.skipped
        ? 'Geçildi.'
        : 'Bu sefer olmadı.';
    let extra: ReactNode = '';
    if (cq.explain) extra = cq.explain(correct, detail);
    else if (!correct) {
      const ans = answerTextOf(q);
      if (ans) extra = `Doğrusu: ${ans}`;
    }
    this.feedback = {
      content: (
        <span>
          <strong>{msg}</strong>
          {extra ? <span> {typeof extra === 'string' ? <Rich text={extra} /> : extra}</span> : null}
        </span>
      ),
      tone: correct ? 'good' : 'bad',
    };
    try {
      q.onFinal?.(correct, detail);
    } catch (e) {
      console.error(e);
    }
    cq.ended = !!(cfg.total && st.answered >= cfg.total) || (st.lives != null && st.lives <= 0);
    this.changed();
    if (st.mode === 'challenge' || cfg.autoAdvance) {
      this.autoNext = setTimeout(
        () => {
          if (this.current === cq) {
            if (cq.ended) this.finish(false);
            else this.nextQuestion();
          }
        },
        correct ? 750 : 1700,
      );
    }
  }

  finish = (aborted: boolean): void => {
    const { st, cfg } = this;
    if (st.over) return;
    st.over = true;
    clearInterval(this.timer);
    this.timer = undefined;
    clearTimeout(this.autoNext);
    clearTimeout(this.autoPlay);
    audio.stopAll();
    if (aborted && st.answered === 0) {
      this.onExit();
      return;
    }
    const acc = st.answered ? st.credit / st.answered : 0;
    const stars =
      st.answered >= Math.min(cfg.total || 5, 5) || cfg.mode === 'challenge'
        ? acc >= 0.9
          ? 3
          : acc >= 0.7
            ? 2
            : acc >= 0.5
              ? 1
              : 0
        : 0;
    const challengeStars = cfg.starAt
      ? st.score >= cfg.starAt[2]
        ? 3
        : st.score >= cfg.starAt[1]
          ? 2
          : st.score >= cfg.starAt[0]
            ? 1
            : 0
      : stars;
    const finalStars = cfg.mode === 'challenge' ? challengeStars : stars;
    const res = saveLevel(cfg.key, { stars: aborted ? 0 : finalStars, acc: aborted ? 0 : acc, score: st.score });
    if (!aborted) award('ilk_antrenman');
    if (!aborted && cfg.total && st.answered >= cfg.total && acc >= 0.999) award('kusursuz');
    try {
      cfg.onFinish?.(st);
    } catch (e) {
      console.error(e);
    }
    this.result = { acc, stars: finalStars, aborted, prev: res.prev, newBest: res.newBest };
    this.phase = 'results';
    this.current = null;
    this.changed();
  };

  /** Called when the runner mounts (twice in React StrictMode development builds). */
  attach(): void {
    this.attached = true;
    this.startListeners();
  }

  /** Called when the runner unmounts: stops timers and listeners. */
  detach(): void {
    this.attached = false;
    clearInterval(this.timer);
    this.timer = undefined;
    clearTimeout(this.autoNext);
    clearTimeout(this.autoPlay);
    this.offMic?.();
    this.offMic = null;
  }
}
